import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { leadsApi, patientsApi, assessmentsApi, invoicesApi, paymentsApi, staffApi } from '../../services/api';
import { userAvatarSrc } from '../../utils/avatarUrl';
import ClinicSwitcher from './ClinicSwitcher';
import {
  HiOutlineMenu,
  HiOutlineBell,
  HiOutlineSearch,
  HiOutlineLogout,
  HiOutlineUser,
  HiOutlineUserCircle,
  HiOutlinePhone
} from 'react-icons/hi';
import { discussionsApi } from '../../services/api';
import { getSocket } from '../../services/socket';
import krishnaLogo from '../../assets/krishnalogo.png';

const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { canRead, isTherapist } = usePermissions();
  const navigate = useNavigate();
  const headerImg = userAvatarSrc(user);
  const [pendingLeaveCount, setPendingLeaveCount] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchDropdownOpen, setSearchDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const MODULE_RESULT_LIMIT = 4;
  const TOTAL_RESULT_LIMIT = 12;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const refreshPendingLeaveCount = useCallback(async () => {
    if (!user || !canRead('staff')) {
      setPendingLeaveCount(0);
      return;
    }
    try {
      const res = await staffApi.getLeaveRequestsPendingCount();
      setPendingLeaveCount(res.data.data?.pending_count ?? 0);
    } catch {
      setPendingLeaveCount(0);
    }
  }, [user, canRead]);

  useEffect(() => {
    refreshPendingLeaveCount();
    const interval = setInterval(refreshPendingLeaveCount, 60000);
    const onChanged = () => refreshPendingLeaveCount();
    window.addEventListener('pendingLeaveRequestsChanged', onChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener('pendingLeaveRequestsChanged', onChanged);
    };
  }, [refreshPendingLeaveCount]);

  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If search query is empty, clear results
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setSearchDropdownOpen(false);
      return;
    }

    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const loadNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const res = await discussionsApi.getNotifications();
      const items = res.data?.data || [];
      setNotifications(items);
      setUnreadNotificationCount(res.data?.unread_count || 0);
    } catch {
      setNotifications([]);
      setUnreadNotificationCount(0);
    }
  }, [user]);

  useEffect(() => {
    loadNotifications();
    const socket = getSocket();
    if (!socket) return undefined;
    const onNotification = () => loadNotifications();
    const onMention = () => loadNotifications();
    socket.on('notification:new', onNotification);
    socket.on('message:mention', onMention);
    return () => {
      socket.off('notification:new', onNotification);
      socket.off('message:mention', onMention);
    };
  }, [loadNotifications]);

  const performSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const [patientsRes, leadsRes, assessmentsRes, invoicesRes, paymentsRes] = await Promise.all([
        patientsApi.getAll({ search: query, limit: MODULE_RESULT_LIMIT, page: 1 }),
        leadsApi.getAll({ search: query, limit: MODULE_RESULT_LIMIT, page: 1 }),
        assessmentsApi.getAll({ search: query, limit: MODULE_RESULT_LIMIT, page: 1 }),
        invoicesApi.getAll({ search: query, limit: MODULE_RESULT_LIMIT, page: 1 }),
        paymentsApi.getAll({ search: query, limit: MODULE_RESULT_LIMIT, page: 1 })
      ]);

      const normalizedQuery = query.trim().toLowerCase();
      const normalizedDigits = normalizedQuery.replace(/\D/g, '');
      const hasDigitQuery = normalizedDigits.length > 0;
      const normalize = (value) => String(value || '').trim().toLowerCase();
      const onlyDigits = (value) => normalize(value).replace(/\D/g, '');
      const scoreResult = ({ title, subtitle, contact, meta }) => {
        const candidates = [title, subtitle, contact, meta?.code, meta?.altContact].filter(Boolean);
        let score = 0;
        for (const candidate of candidates) {
          const candidateText = normalize(candidate);
          const candidateDigits = onlyDigits(candidate);
          if (hasDigitQuery && candidateDigits) {
            if (candidateDigits === normalizedDigits) score = Math.max(score, 300);
            else if (candidateDigits.startsWith(normalizedDigits)) score = Math.max(score, 260);
            else if (candidateDigits.includes(normalizedDigits)) score = Math.max(score, 230);
          }
          if (candidateText === normalizedQuery) score = Math.max(score, 220);
          else if (candidateText.startsWith(normalizedQuery)) score = Math.max(score, 180);
          else if (candidateText.includes(normalizedQuery)) score = Math.max(score, 140);
        }
        return score;
      };

      const mapPatient = (p) => ({
        type: 'patient',
        id: p.id,
        title: p.full_name || `Patient #${p.id}`,
        subtitle: p.patient_code || null,
        contact: p.contact_primary || p.contact_whatsapp || null,
        route: `/patients/${p.id}`,
        meta: { code: p.patient_code, altContact: p.contact_whatsapp, label: 'Patient' }
      });
      const mapLead = (l) => ({
        type: 'lead',
        id: l.id,
        title: l.full_name || `Lead #${l.id}`,
        subtitle: l.status_name || null,
        contact: l.contact_primary || l.contact_whatsapp || null,
        route: `/leads/${l.id}`,
        meta: { code: l.id, altContact: l.contact_whatsapp, label: 'Lead' }
      });
      const mapAssessment = (a) => ({
        type: 'assessment',
        id: a.id,
        title: a.patient_name || a.lead_name || `Assessment #${a.id}`,
        subtitle: a.assessment_date || null,
        contact: a.patient_contact_primary || a.lead_contact || null,
        route: `/assessments?focusId=${a.id}&search=${encodeURIComponent(a.patient_name || a.lead_name || '')}`,
        meta: { code: a.id, altContact: a.patient_contact_whatsapp, label: 'Assessment' }
      });
      const mapInvoice = (i) => ({
        type: 'invoice',
        id: i.id,
        title: i.patient_name || i.lead_name || i.invoice_number || `Invoice #${i.id}`,
        subtitle: i.invoice_number || null,
        contact: i.contact_primary || i.lead_contact_primary || null,
        route: `/invoices?focusId=${i.id}&search=${encodeURIComponent(i.invoice_number || i.patient_name || '')}`,
        meta: { code: i.invoice_number, altContact: i.contact_whatsapp || i.lead_contact_whatsapp, label: 'Invoice' }
      });
      const mapPayment = (p) => ({
        type: 'payment',
        id: p.id,
        title: p.patient_name || p.lead_name || p.receipt_number || `Payment #${p.id}`,
        subtitle: p.receipt_number || p.invoice_number || null,
        contact: p.contact_primary || p.lead_contact_primary || null,
        route: `/payments?focusId=${p.id}&search=${encodeURIComponent(p.receipt_number || p.invoice_number || '')}`,
        meta: { code: p.receipt_number || p.invoice_number, altContact: p.contact_whatsapp || p.lead_contact_whatsapp, label: 'Payment' }
      });

      const allResults = [
        ...(patientsRes.data.data.patients || []).map(mapPatient),
        ...(leadsRes.data.data.leads || []).map(mapLead),
        ...(assessmentsRes.data.data.assessments || []).map(mapAssessment),
        ...(invoicesRes.data.data.invoices || []).map(mapInvoice),
        ...(paymentsRes.data.data.payments || []).map(mapPayment)
      ]
        .map((item) => ({ ...item, rankScore: scoreResult(item) }))
        .sort((a, b) => b.rankScore - a.rankScore)
        .slice(0, TOTAL_RESULT_LIMIT);

      const results = allResults;
      setSearchResults(results);
      setSearchDropdownOpen(results.length > 0);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleResultClick = (result) => {
    navigate(result.route);
    setSearchQuery('');
    setSearchResults([]);
    setSearchDropdownOpen(false);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && searchResults.length > 0) {
      handleResultClick(searchResults[0]);
    } else if (e.key === 'Escape') {
      setSearchDropdownOpen(false);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const openNotifications = async () => {
    setNotificationsOpen(true);
    await loadNotifications();
  };

  const markAllNotificationsRead = async () => {
    await discussionsApi.readAllNotifications();
    await loadNotifications();
  };

  const handleNotificationClick = async (item) => {
    await discussionsApi.readNotification(item.id);
    setNotificationsOpen(false);
    if (item.channel_id) {
      navigate(`/discussions?channelId=${item.channel_id}&messageId=${item.message_id || ''}`);
    } else {
      navigate('/discussions');
    }
    loadNotifications();
  };

  return (
    <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
      <div className="flex h-16 items-center justify-between gap-2 px-3 sm:px-4 lg:px-6">
        {/* Left side */}
        <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4 lg:flex-initial">
          {/* Therapist mobile: clinic logo (desktop uses sidebar logo) */}
          {isTherapist && (
            <Link
              to="/dashboard"
              className="flex shrink-0 items-center py-1 lg:hidden"
              aria-label="Dashboard"
            >
              <img
                src={krishnaLogo}
                alt="Krishna Pediatric Rehabilitation Clinic"
                className="h-12 w-auto max-w-[min(72vw,17rem)] object-contain sm:h-14 sm:max-w-[20rem]"
              />
            </Link>
          )}
          {/* Mobile menu (hidden for therapist — bottom tab bar in Sidebar) */}
          {!isTherapist && (
            <button
              type="button"
              onClick={onMenuClick}
              className="lg:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100"
            >
              <HiOutlineMenu className="w-6 h-6" />
            </button>
          )}

          {/* Search */}
          <div className="hidden md:flex items-center">
            <div className="relative" ref={searchRef}>
              <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search patients, leads, assessments, invoices, payments..."
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setSearchDropdownOpen(true);
                  }
                }}
                className="w-52 lg:w-72 xl:w-80 pl-10 pr-4 py-2.5 bg-gray-50 border-0 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:bg-white"
              />
              
              {/* Search Results Dropdown */}
              {searchDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 lg:w-80 bg-white rounded-xl shadow-lg border border-gray-200 max-h-96 overflow-y-auto z-50 animate-slide-down">
                  {searchLoading ? (
                    <div className="p-4 text-center text-gray-500">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent mx-auto"></div>
                      <p className="mt-2 text-sm">Searching...</p>
                    </div>
                  ) : searchResults.length > 0 ? (
                    <div className="py-2">
                      {searchResults.map((result) => (
                        <button
                          key={`${result.type}-${result.id}`}
                          onClick={() => handleResultClick(result)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-start gap-3">
                            <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                              result.type === 'patient' ? 'bg-blue-100' : 'bg-green-100'
                            }`}>
                              {result.type === 'patient' ? (
                                <HiOutlineUserCircle className={`w-5 h-5 ${result.type === 'patient' ? 'text-blue-600' : 'text-green-600'}`} />
                              ) : (
                                <HiOutlineUser className={`w-5 h-5 ${
                                  result.type === 'lead'
                                    ? 'text-green-600'
                                    : result.type === 'assessment'
                                      ? 'text-purple-600'
                                      : result.type === 'invoice'
                                        ? 'text-amber-600'
                                        : 'text-emerald-600'
                                }`} />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {result.title}
                                </p>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  result.type === 'patient' 
                                    ? 'bg-blue-100 text-blue-700' 
                                    : result.type === 'lead'
                                      ? 'bg-green-100 text-green-700'
                                      : result.type === 'assessment'
                                        ? 'bg-purple-100 text-purple-700'
                                        : result.type === 'invoice'
                                          ? 'bg-amber-100 text-amber-700'
                                          : 'bg-emerald-100 text-emerald-700'
                                }`}>
                                  {result.meta?.label || (result.type === 'patient'
                                    ? 'Patient'
                                    : result.type === 'lead'
                                      ? 'Lead'
                                      : result.type === 'assessment'
                                        ? 'Assessment'
                                        : result.type === 'invoice'
                                          ? 'Invoice'
                                          : 'Payment')}
                                </span>
                              </div>
                              {result.subtitle && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {result.subtitle}
                                </p>
                              )}
                              {result.contact && (
                                <div className="flex items-center gap-1 mt-1">
                                  <HiOutlinePhone className="w-3 h-3 text-gray-400" />
                                  <p className="text-xs text-gray-500">{result.contact}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : searchQuery.trim() ? (
                    <div className="p-4 text-center text-gray-500">
                      <p className="text-sm">No results found</p>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-1 sm:gap-3">
          {canRead('staff') && pendingLeaveCount > 0 && (
            <button
              type="button"
              onClick={() => navigate('/staff')}
              className="relative flex items-center justify-center min-h-9 min-w-9 px-2 rounded-lg bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 text-xs font-bold"
              title={`${pendingLeaveCount} pending leave request(s)`}
            >
              {pendingLeaveCount > 99 ? '99+' : pendingLeaveCount}
            </button>
          )}
          <div className="hidden md:block">
            <ClinicSwitcher />
          </div>
          {/* Notifications */}
          <button onClick={openNotifications} className="relative min-h-[44px] min-w-[44px] p-2 rounded-lg text-gray-500 hover:bg-gray-100">
            <HiOutlineBell className="w-6 h-6" />
            {unreadNotificationCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-danger-500 text-white text-[10px] rounded-full flex items-center justify-center">
                {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
              </span>
            )}
          </button>

          {/* User dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-3 p-1.5 rounded-lg hover:bg-gray-100"
            >
              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center overflow-hidden shrink-0">
                {headerImg ? (
                  <img src={headerImg} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-primary-700 font-semibold text-sm">
                    {user?.full_name?.charAt(0) || 'U'}
                  </span>
                )}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            </button>

            {/* Dropdown menu */}
            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-1 animate-slide-down">
                <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                  <div className="w-11 h-11 rounded-full bg-primary-100 overflow-hidden shrink-0 flex items-center justify-center">
                    {headerImg ? (
                      <img src={headerImg} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-primary-700 font-semibold">{user?.full_name?.charAt(0) || 'U'}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{user?.full_name}</p>
                    <p className="text-xs text-gray-500 capitalize truncate">{user?.role}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setDropdownOpen(false);
                    navigate('/profile');
                  }}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <HiOutlineUser className="w-4 h-4" />
                  Profile
                </button>
                <hr className="my-1 border-gray-200" />
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm text-danger-500 hover:bg-danger-50"
                >
                  <HiOutlineLogout className="w-4 h-4" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {notificationsOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-end p-4" onClick={() => setNotificationsOpen(false)}>
          <div className="w-full max-w-md bg-white rounded-xl border border-gray-200 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
              <button onClick={markAllNotificationsRead} className="text-xs text-primary-700 hover:underline">
                Mark all as read
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-4 text-sm text-gray-500">No notifications yet.</div>
              ) : (
                notifications.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleNotificationClick(item)}
                    className={`w-full text-left p-4 border-b border-gray-100 hover:bg-gray-50 ${item.is_read ? 'bg-white' : 'bg-primary-50/40'}`}
                  >
                    <p className="text-xs uppercase tracking-wide text-gray-500">{item.event_type}</p>
                    <p className="text-sm font-medium text-gray-900">{item.title}</p>
                    <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{item.body}</p>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;

